import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  Visibility as VisibilityIcon,
  ImageNotSupported as PlaceholderIcon,
} from '@mui/icons-material';
import { GalleryItem } from '../api/galleryApi';
import brand from '../../../config/brand';

interface GalleryCardProps {
  item: GalleryItem;
}

const GalleryCard: React.FC<GalleryCardProps> = ({ item }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const thumbnail =
    item.media?.[0]?.thumbnailUrl || item.media?.[0]?.url || null;

  return (
    <Card
      onClick={() => navigate(`/gallery/${item._id}`)}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.06)',
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 24px 64px rgba(0,0,0,0.5)'
              : '0 24px 64px rgba(0,0,0,0.15)',
          borderColor: `rgba(${brand.colorsRgb.fire}, 0.4)`,
          '& .gallery-overlay': {
            opacity: 1,
          },
        },
      }}
    >
      {/* Thumbnail */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        {thumbnail ? (
          <CardMedia
            component="img"
            height={200}
            image={thumbnail}
            alt={item.title}
            sx={{
              objectFit: 'cover',
              transition: 'transform 0.4s ease',
              '.MuiCard-root:hover &': {
                transform: 'scale(1.05)',
              },
            }}
          />
        ) : (
          <Box
            sx={{
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(0,0,0,0.03)',
            }}
          >
            <PlaceholderIcon
              sx={{ fontSize: 60, color: 'text.disabled' }}
            />
          </Box>
        )}

        {/* Hover overlay */}
        <Box
          className="gallery-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7) 100%)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            display: 'flex',
            alignItems: 'flex-end',
            p: 2,
          }}
        >
          <Typography variant="body2" color="white" fontWeight={600}>
            View Details
          </Typography>
        </Box>

        {/* Category chip */}
        {item.category && (
          <Chip
            label={item.category}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              bgcolor: `rgba(${brand.colorsRgb.fire}, 0.9)`,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 24,
              backdropFilter: 'blur(8px)',
            }}
          />
        )}
      </Box>

      {/* Content */}
      <CardContent sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mb: 0.5,
            fontSize: '1rem',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          by {item.authorName}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            flex: 1,
            mb: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.5,
          }}
        >
          {item.description}
        </Typography>

        {/* Tags */}
        {item.tags?.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
            {item.tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.15)'
                      : 'rgba(0,0,0,0.12)',
                }}
              />
            ))}
            {item.tags.length > 3 && (
              <Typography variant="caption" color="text.disabled">
                +{item.tags.length - 3}
              </Typography>
            )}
          </Box>
        )}

        {/* Stats */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mt: 'auto',
            pt: 1.5,
            borderTop: '1px solid',
            borderColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.06)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FavoriteIcon
              sx={{
                fontSize: 16,
                color: item.hasVoted ? brand.colors.fire : 'text.disabled',
              }}
            />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {item.voteCount || 0}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <VisibilityIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {item.viewCount || 0}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GalleryCard;
